package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.marketing.MkIcon
import co.maytes.standupbot.dashboard.components.marketing.PageIntro
import co.maytes.standupbot.dashboard.components.motion.Reveal
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.styles.Marketing
import co.maytes.standupbot.dashboard.styles.MarketingIcon
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

@Page("/contact")
@Composable
fun ContactPage() {
    MarketingShell {
        Div(attrs = { classes(Marketing.PAGE_CLASS) }) {
            PageIntro(
                title = "Get in touch.",
                line = "We reply to every message. Three inboxes, three humans, no bot in between.",
            )
            LanesSection()
            FormSection()
        }
    }
}

private data class ContactLane(
    val heading: String,
    val email: String,
    val blurb: String,
    val responseSla: String,
    val icon: MarketingIcon,
)

private val lanes = listOf(
    ContactLane(
        heading = "Support",
        email = "support@standupbot.app",
        blurb = "Bugs and standups that won't land in Slack.",
        responseSla = "Usually same business day",
        icon = MarketingIcon.Lifebuoy,
    ),
    ContactLane(
        heading = "Sales",
        email = "sales@standupbot.app",
        blurb = "Pricing, procurement, the Business plan and annual contracts.",
        responseSla = "Usually one to two business days",
        icon = MarketingIcon.Building,
    ),
    ContactLane(
        heading = "Security",
        email = "security@standupbot.app",
        blurb = "Vulnerability reports and responsible disclosure.",
        responseSla = "PGP available and prioritized",
        icon = MarketingIcon.Shield,
    ),
)

@Composable
private fun LanesSection() {
    Section(attrs = { classes("mk-section", "mk-section--tight") }) {
        Div(attrs = { classes("mk-wrap", "mk-guides") }) {
            lanes.forEachIndexed { index, lane ->
                Reveal(index = index, modifier = Modifier.classNames("mk-guide")) {
                    MkIcon(lane.icon)
                    H3(attrs = { classes("mk-dtitle") }) { Text(lane.heading) }
                    P(attrs = { classes("mk-body") }) { Text(lane.blurb) }
                    Link(
                        path = "mailto:${lane.email}",
                        text = lane.email,
                        modifier = Modifier.classNames("mk-mail"),
                    )
                    Span(attrs = { classes("mk-sla") }) { Text(lane.responseSla) }
                }
            }
        }
    }
}

@Composable
private fun FormSection() {
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    Section(attrs = { classes("mk-section", "mk-band") }) {
        Div(attrs = { classes("mk-wrap", "mk-narrow") }) {
            Reveal(modifier = Modifier.classNames("mk-formcard")) {
                H2(attrs = { classes("mk-dtitle") }) { Text("Ship us a note") }
                P(attrs = { classes("mk-body") }) {
                    Text("Feedback, partnerships, a job application. We read everything.")
                }
                Div(attrs = { classes("mk-fields") }) {
                    FieldLabel("Your name")
                    TextInput(value = name, onValueChange = { name = it }, placeholder = "Ada Lovelace")
                    FieldLabel("Email")
                    TextInput(value = email, onValueChange = { email = it }, placeholder = "ada@example.com")
                    FieldLabel("Message")
                    TextArea(
                        attrs = Modifier.toAttrs {
                            classes("mk-textarea")
                            placeholder("What's on your mind?")
                            attr("rows", "6")
                        },
                    )
                }
                Div(attrs = { classes("mk-actions") }) {
                    Button(
                        label = "Send it",
                        variant = ButtonVariant.Primary,
                        onClick = { kotlinx.browser.window.location.href = "mailto:support@standupbot.app" },
                    )
                }
            }
        }
    }
}

@Composable
private fun FieldLabel(text: String) {
    Span(attrs = { classes("mk-field-label") }) { Text(text) }
}
